import { useCallback, useEffect, useState } from "react";

/**
 * Tema del panel (decisión §4.3). La clase "dark" se pone en el mismo elemento
 * .admin (no en <html>), así la web pública no cambia. Se guarda en
 * localStorage["tandil_admin_theme"]; sin valor guardado, sigue al sistema.
 */
export type AdminTheme = "light" | "dark";

const STORAGE_KEY = "tandil_admin_theme";

function readStored(): AdminTheme | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === "light" || value === "dark" ? value : null;
  } catch {
    return null;
  }
}

function systemTheme(): AdminTheme {
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  } catch {
    return "light";
  }
}

function store(theme: AdminTheme): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // modo privado o almacenamiento bloqueado: el tema vale solo para esta visita
  }
}

export function useAdminTheme() {
  const [theme, setThemeState] = useState<AdminTheme>(() => {
    if (typeof window === "undefined") return "light";
    return readStored() ?? systemTheme();
  });

  // Mientras la persona no elija, el panel sigue los cambios del sistema.
  useEffect(() => {
    if (typeof window === "undefined" || readStored()) return;
    let media: MediaQueryList;
    try {
      media = window.matchMedia("(prefers-color-scheme: dark)");
    } catch {
      return;
    }
    const onChange = (e: MediaQueryListEvent) => {
      if (!readStored()) setThemeState(e.matches ? "dark" : "light");
    };
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  const setTheme = useCallback((next: AdminTheme) => {
    store(next);
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const next: AdminTheme = prev === "dark" ? "light" : "dark";
      store(next);
      return next;
    });
  }, []);

  return { theme, setTheme, toggleTheme };
}
